import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, Users } from "@phosphor-icons/react";

const CATEGORIES = [
    { value: "", label: "All" },
    { value: "singing", label: "Singing" },
    { value: "dancing", label: "Dancing" },
    { value: "comedy", label: "Comedy" },
    { value: "rap", label: "Rap" },
    { value: "modeling", label: "Modeling" },
    { value: "trivia", label: "Trivia" },
    { value: "invention", label: "Invention" },
];

const TIER_COLORS = {
    entry: "border-white/20 text-white",
    standard: "border-wta-secondary text-wta-secondary",
    elite: "border-wta-primary text-wta-primary",
    superstar: "border-wta-accent text-wta-accent",
};

export default function Competitions() {
    const [items, setItems] = useState([]);
    const [category, setCategory] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/competitions" + (category ? `?category=${category}` : "")).then(({ data }) => setItems(data)).finally(() => setLoading(false));
    }, [category]);

    return (
        <div className="px-4 md:px-10 py-8 max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-2">
                <div>
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">THE ARENA</div>
                    <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">Competitions</h1>
                </div>
            </div>
            <p className="text-wta-muted mb-8">32 players. 5 rounds. One champion. 70% of entries become the prize pool.</p>

            <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8 -mx-1 px-1">
                {CATEGORIES.map((c) => (
                    <button
                        key={c.value}
                        data-testid={`filter-${c.value || "all"}`}
                        onClick={() => setCategory(c.value)}
                        className={`shrink-0 px-4 py-2 rounded-sm border text-sm font-heading tracking-wider uppercase transition ${
                            category === c.value ? "bg-wta-primary border-wta-primary text-white" : "border-wta-border text-white/70 hover:border-white"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-wta-muted">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-wta-muted text-center py-12">No competitions in this category yet.</div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((c) => (
                        <Link key={c.id} to={`/competitions/${c.id}`} data-testid={`comp-card-${c.id}`} className="group bg-wta-surface border border-wta-border rounded-sm overflow-hidden hover:border-wta-primary transition">
                            <div className="aspect-video relative overflow-hidden bg-wta-surface2">
                                {c.cover_image && <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition" />}
                                <div className="absolute top-3 left-3 flex gap-2">
                                    <span className={`tier-badge bg-black/60 backdrop-blur ${TIER_COLORS[c.tier]}`}>{c.tier}</span>
                                </div>
                                <div className="absolute bottom-3 right-3 glass px-2 py-1 rounded-sm text-xs tracking-wider uppercase">
                                    {c.status === "registering" ? "Open" : c.status === "active" ? `Round ${c.current_round}` : "Finished"}
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="text-xs tracking-widest uppercase text-wta-muted mb-1">
                                    {c.category}
                                    {c.category === "modeling" && c.gender && (
                                        <span className={`ml-2 px-1.5 py-0.5 rounded-sm text-[9px] ${c.gender === "female" ? "bg-pink-500/20 text-pink-300" : "bg-blue-500/20 text-blue-300"}`}>
                                            {c.gender === "female" ? "♀ Female" : "♂ Male"}
                                        </span>
                                    )}
                                </div>
                                <div className="font-heading text-2xl tracking-wide leading-tight">{c.title}</div>
                                <div className="mt-3 flex items-center justify-between text-xs text-wta-muted">
                                    <span className="flex items-center gap-1"><Users size={14} /> {c.entries_count}/32</span>
                                    <span className="flex items-center gap-1 text-wta-accent"><Trophy size={14} weight="fill" /> ${c.prize_pool.toFixed(0)} pool</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
